import type { ITextObject } from './types/text-object';

const POSITION_EPSILON = 1e-9;

function pointsEqual(
  a: ITextObject['insertionPoint'],
  b: ITextObject['insertionPoint']
): boolean {
  return (
    Math.abs(a.x - b.x) < POSITION_EPSILON &&
    Math.abs(a.y - b.y) < POSITION_EPSILON &&
    Math.abs(a.z - b.z) < POSITION_EPSILON
  );
}

function metaDataEqual(
  a: ITextObject['metaData'],
  b: ITextObject['metaData']
): boolean {
  if (a === b) {
    return true;
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every((key) => a[key] === b[key]);
}

/** Full equality for upsert diffing (replaces JSON.stringify). */
export function textsEqual(a: ITextObject, b: ITextObject): boolean {
  return (
    a.guid === b.guid &&
    a.content === b.content &&
    pointsEqual(a.insertionPoint, b.insertionPoint) &&
    a.textHeight === b.textHeight &&
    a.rotation === b.rotation &&
    a.horizontalAttachment === b.horizontalAttachment &&
    a.verticalAttachment === b.verticalAttachment &&
    a.typefaceName === b.typefaceName &&
    a.widthFactor === b.widthFactor &&
    a.obliqueAngle === b.obliqueAngle &&
    a.boxWidth === b.boxWidth &&
    a.lineSpacingFactor === b.lineSpacingFactor &&
    a.lineSpacingStyle === b.lineSpacingStyle &&
    a.drawDirection === b.drawDirection &&
    a.color === b.color &&
    a.editable === b.editable &&
    a.modelSpaceId === b.modelSpaceId &&
    metaDataEqual(a.metaData, b.metaData)
  );
}

/** Appearance fields that require canvas/texture rebuild. */
export function textAppearanceEqual(a: ITextObject, b: ITextObject): boolean {
  return (
    a.content === b.content &&
    a.textHeight === b.textHeight &&
    a.horizontalAttachment === b.horizontalAttachment &&
    a.verticalAttachment === b.verticalAttachment &&
    a.typefaceName === b.typefaceName &&
    a.widthFactor === b.widthFactor &&
    a.obliqueAngle === b.obliqueAngle &&
    a.boxWidth === b.boxWidth &&
    a.lineSpacingFactor === b.lineSpacingFactor &&
    a.lineSpacingStyle === b.lineSpacingStyle &&
    a.drawDirection === b.drawDirection &&
    a.color === b.color
  );
}

/** Transform fields that only update mesh position/rotation. */
export function textTransformEqual(a: ITextObject, b: ITextObject): boolean {
  return (
    pointsEqual(a.insertionPoint, b.insertionPoint) && a.rotation === b.rotation
  );
}
